// src/components/login/login-page.tsx
//
// -- Migrated onto the kit, Phase 6 ------------------------------------------
//
// The first screen to move, and the only one this session could see: it is the
// one route that renders with no backend and no session, so before and after
// are two screenshots of the same page rather than two arguments about it.
//
// What moved: the imports, and `data-kit` on the root, which is the whole of
// the migration switch (DECISIONS.md D23). `ui/input`, `ui/button`,
// `ui/checkbox` and `ui/alert` are gone; TextInput, PasswordInput, Button,
// Checkbox, Banner and AuthCard are in.
//
// What did NOT move, deliberately and by instruction: every handler, every
// piece of state, `authApi.login`, the three mfa_state branches, the SSO probe
// and every error string. This is a presentation-layer change. Everything above
// the `return` is unchanged.
//
// Two things the kit made better rather than merely different, and both were
// defects rather than preferences:
//
//   * The password reveal was a bare <button> with an inline style and no
//     accessible name. It is `PasswordInput` now, which owns its reveal and
//     labels it.
//   * The submit button greyed itself out through `background: var(--muted)`
//     while staying enabled to a screen reader whenever `!email || !password`.
//     `Button` takes `disabled` and paints from `--i-dis-bg`, so the look and
//     the state are one fact.
import { useState, useEffect, FormEvent } from "react";
import { UpmodeLogo } from "./upmode-logo";
import { authApi, type LoginResponse } from "@/api/auth";
import { getSsoAvailability, startSsoLogin } from "@/api/sso";
import {
  AuthCard, Banner, Button, Checkbox, FieldRow, FormStack, InlineLink,
  PasswordInput, TextInput,
} from "../kit";

/**
 * The login page now resolves to ONE of three branches based on
 * the response's mfa_state:
 *
 *   "none"            → onLoginSuccess(token) — same as before
 *   "setup_required"  → onMfaSetupRequired(setup_token, username, ...)
 *   "challenge"       → onMfaChallenge(challenge_token, username, ...)
 *
 * App.tsx renders the corresponding next page.
 */
interface LoginPageProps {
  onLoginSuccess: (token: string, payload: {
    username: string;
    is_admin: boolean;
    user_type: string;
    full_name?: string | null;
    rememberMe: boolean;
  }) => void;
  onMfaSetupRequired: (setup_token: string, payload: {
    username: string;
    is_admin: boolean;
    user_type: string;
    full_name?: string | null;
    rememberMe: boolean;
  }) => void;
  onMfaChallenge: (challenge_token: string, payload: {
    username: string;
    is_admin: boolean;
    user_type: string;
    full_name?: string | null;
    rememberMe: boolean;
  }) => void;

  onNavigateToSignup?: () => void;
  onNavigateToTerms?: () => void;
  onNavigateToPrivacy?: () => void;

  /** Seed an error message (e.g. a failed SSO redirect handed back by App). */
  initialError?: string | null;
}

export function LoginPage({
  onLoginSuccess,
  onMfaSetupRequired,
  onMfaChallenge,
  onNavigateToSignup,
  onNavigateToTerms,
  onNavigateToPrivacy,
  initialError,
}: LoginPageProps) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(true);
  // `showPassword` lived here to drive a hand-rolled reveal button.
  // PasswordInput owns its reveal, so the state has no reader left. Removed
  // rather than kept: it is presentation state, and the component it existed
  // for is gone.

  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(initialError || "");

  const [ssoEnabled, setSsoEnabled] = useState(false);
  // Customer SSO connections are chosen by email domain, so the button needs
  // the email typed above before it knows which IdP to send the user to.
  const [ssoNeedsEmail, setSsoNeedsEmail] = useState(false);
  const [ssoLoading, setSsoLoading] = useState(false);

  useEffect(() => {
    let alive = true;
    getSsoAvailability().then(({ enabled, needsEmail }) => {
      if (!alive) return;
      setSsoEnabled(enabled);
      setSsoNeedsEmail(needsEmail);
    });
    return () => { alive = false; };
  }, []);

  const handleSsoLogin = async () => {
    setErrorMsg("");
    const workEmail = email.includes("@") ? email : "";
    if (ssoNeedsEmail && !workEmail) {
      setErrorMsg("Enter your work email above, then choose Sign in with SSO.");
      return;
    }
    setSsoLoading(true);
    try {
      await startSsoLogin(workEmail);   // navigates away to the IdP on success
    } catch (err: any) {
      setSsoLoading(false);
      setErrorMsg(err?.message || "Couldn't start SSO sign-in. Please try again.");
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setErrorMsg("");
    setIsLoading(true);

    try {
      const data: LoginResponse = await authApi.login({
        username: email.trim(),
        password,
        rememberMe,
      });

      const basePayload = {
        username:  data.username,
        is_admin:  !!data.is_admin,
        user_type: data.user_type || "basic",
        full_name: data.full_name,
        rememberMe,
      };

      if (data.mfa_state === "setup_required") {
        if (!data.setup_token) throw new Error("Server did not return a setup token.");
        onMfaSetupRequired(data.setup_token, basePayload);
        return;
      }

      if (data.mfa_state === "challenge") {
        if (!data.challenge_token) throw new Error("Server did not return a challenge token.");
        onMfaChallenge(data.challenge_token, basePayload);
        return;
      }

      // mfa_state === "none"
      if (!data.access_token) throw new Error("Server did not return an access token.");
      onLoginSuccess(data.access_token, basePayload);
    } catch (err: any) {
      const raw = String(err?.message || "");
      const isBadCreds =
        raw.includes("Invalid username or password") ||
        raw.toLowerCase().includes("401");

      setErrorMsg(
        isBadCreds
          ? "That email or password didn't match. Double-check and try again."
          : raw || "We couldn't sign you in right now. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    // data-kit turns on the kit base layer for this subtree only. It is the
    // migration switch, and it is one attribute.
    <div className="upm-auth-page" data-kit>
      <div className="upm-auth-page__wash" aria-hidden="true" />

      <div className="upm-auth-page__column">
        <div className="upm-auth-page__brand">
          <UpmodeLogo />
        </div>

        <AuthCard title="Welcome back">
          {errorMsg ? <Banner tone="error">{errorMsg}</Banner> : null}

          <form onSubmit={handleSubmit}>
            <FormStack>
              <FieldRow label="Email address" required>
                {({ id }) => (
                  <TextInput
                    id={id}
                    type="email"
                    placeholder="you@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    state={isLoading ? "disabled" : undefined}
                  />
                )}
              </FieldRow>

              <FieldRow label="Password" required>
                {({ id }) => (
                  // The reveal belongs to the component, so it has an
                  // accessible name. The previous one was a bare button with an
                  // inline style and no label at all.
                  <PasswordInput
                    id={id}
                    placeholder="Your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    state={isLoading ? "disabled" : undefined}
                  />
                )}
              </FieldRow>

              <Checkbox
                checked={rememberMe}
                onChange={(v) => setRememberMe(v === true)}
                label="Remember me"
                state={isLoading ? "disabled" : undefined}
              />

              <Button
                type="submit"
                disabled={isLoading || !email || !password}
                loading={isLoading}
                loadingLabel="Signing in"
                className="upm-auth-page__submit"
              >
                Sign in
              </Button>

              {ssoEnabled ? (
                <>
                  <div className="upm-auth-page__or">
                    <span className="upm-auth-page__rule" />
                    <span className="k-meta">or</span>
                    <span className="upm-auth-page__rule" />
                  </div>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={handleSsoLogin}
                    disabled={isLoading || ssoLoading}
                    loading={ssoLoading}
                    loadingLabel="Redirecting"
                    className="upm-auth-page__submit"
                  >
                    Sign in with SSO
                  </Button>
                </>
              ) : null}
            </FormStack>
          </form>

          <p className="upm-auth-page__legal">
            By signing in, you agree to our{" "}
            <InlineLink
              href="#"
              onClick={(e) => { e.preventDefault(); onNavigateToTerms?.(); }}
            >
              Terms of Service
            </InlineLink>{" "}
            and{" "}
            <InlineLink
              href="#"
              onClick={(e) => { e.preventDefault(); onNavigateToPrivacy?.(); }}
            >
              Privacy Policy
            </InlineLink>.
          </p>
        </AuthCard>

        {onNavigateToSignup ? (
          <p className="upm-auth-page__legal">
            Do not have an account?{" "}
            <InlineLink
              href="#"
              onClick={(e) => { e.preventDefault(); if (!isLoading) onNavigateToSignup(); }}
            >
              Sign up
            </InlineLink>
          </p>
        ) : null}
      </div>
    </div>
  );
}
